import { pluginUpdateRecords } from './updateRecords';
import type { PluginUpdateOutcome, SystemUpdateStatus, UpdateState } from './types';
import type { LocaleDict } from './i18n/types';

export type UpdateDisplayState = 'queued' | 'downloading' | UpdateState | PluginUpdateOutcome | 'noUpdate';
/** Settings badges and dock progress share the same state wording. */
export function updateDisplayLabel(state: UpdateDisplayState, labels: LocaleDict['updateStatus']): string {
  const keys = { 'needs-consent': 'needsConsent', 'not-licensed': 'notLicensed' } as const;
  return labels[state === 'needs-consent' || state === 'not-licensed' ? keys[state] : state];
}
export interface UpdateProgress { state: UpdateDisplayState; requestId: string | null; busy: boolean; detail?: string }
const updateJournalActive = (state: UpdateState) => state !== 'committed' && state !== 'rolledBack' && state !== 'blocked' && state !== 'needsOperator' && state !== 'noUpdate';
export const updateStatusActive = (status?: SystemUpdateStatus) => !!status && (
  !!status.queue.core || status.queue.plugins.length > 0 || !!status.journal && status.journal.state !== 'needsOperator'
  || !!status.report?.operations?.some(op => op.state === 'downloading')
);

/** Queued work takes precedence over an older report for the same name. The request id travels with
 *  every verdict; a caller waiting for one request must never accept another request's outcome. */
export function pluginUpdateProgress(status: SystemUpdateStatus | undefined, name: string, requestId?: string): UpdateProgress | null {
  if (!status) return null;
  const { queued, outcome, running, preparation } = pluginUpdateRecords(status, name, requestId);
  if (queued) return { state: 'queued', requestId: queued.requestId, busy: true };
  if (running && status.journal && (updateJournalActive(status.journal.state) || status.journal.state === 'needsOperator')) return {
    state: status.journal.state, requestId: running.requestId, busy: updateJournalActive(status.journal.state),
    ...(status.journal.state === 'needsOperator' && outcome?.requestId === running.requestId
      ? { detail: outcome.detail ?? status.report?.note } : {}),
  };
  if (preparation && (preparation.state === 'downloading' || !outcome || preparation.at >= status.report!.at)) return {
    state: preparation.state, requestId: preparation.requestId, busy: preparation.state === 'downloading',
    ...(preparation.error === null ? {} : { detail: preparation.error }),
  };
  if (outcome) return {
    state: status.report?.state === 'needsOperator' ? 'needsOperator' : outcome.outcome,
    requestId: outcome.requestId, busy: false, detail: outcome.detail ?? status.report?.note,
  };
  // Normalization, disable/uninstall and later runs can retire the id from every retained record.
  if (requestId) return { state: 'superseded', requestId, busy: false };
  return null;
}

/** The finished states a person still has to look at, each with the tone its badge reads in. Every other
 *  state is either running (quiet until it ends) or history that Settings does not keep on screen. */
const ATTENTION_TONE: Partial<Record<UpdateDisplayState, 'danger' | 'warning' | 'muted'>> = {
  failed: 'danger', blocked: 'warning', needsOperator: 'danger', 'needs-consent': 'warning', 'not-licensed': 'warning', rolledBack: 'muted',
};
export const updateStateTone = (state: UpdateDisplayState) => ATTENTION_TONE[state] ?? 'muted';

/** One record of update work Settings shows: `name` is null for the system run itself. */
interface UpdateStatusItem { key: string; name: string | null; progress: UpdateProgress; detail?: string; updatesHeld?: true }

/** What the System status card lists: queued and running work, and finished work only where it needs a
 *  person. A clean outcome is left out unless its release check observed a publisher hold. */
export function updateStatusItems(status: SystemUpdateStatus): UpdateStatusItem[] {
  const items: UpdateStatusItem[] = [];
  const { queue, journal, report } = status;
  if (queue.core) items.push({ key: 'core-queued', name: null, progress: { state: 'queued', busy: true, requestId: queue.core.requestId } });
  if (journal) items.push({
    key: 'core-run', name: null, progress: { state: journal.state, busy: false, requestId: journal.coreRequest?.requestId ?? null },
    ...(report?.note !== undefined && report.at >= journal.startedAt ? { detail: report.note } : {}),
    ...(report?.updatesHeld && report.at >= journal.startedAt ? { updatesHeld: true } : {}),
  });
  else if (report && (ATTENTION_TONE[report.state] || report.updatesHeld)) items.push({
    key: 'core-report', name: null, progress: { state: report.state, busy: false, requestId: null },
    ...(report.note === undefined ? {} : { detail: report.note }),
    ...(report.updatesHeld ? { updatesHeld: true } : {}),
  });
  const names = new Set([
    ...queue.plugins.map(op => op.name),
    ...journal?.ops.map(op => op.name) ?? [],
    ...report?.plugins.map(op => op.name) ?? [],
    ...report?.operations?.flatMap(op => op.name === null ? [] : [op.name]) ?? [],
  ]);
  for (const name of names) {
    const progress = pluginUpdateProgress(status, name);
    if (progress && (progress.busy || ATTENTION_TONE[progress.state])) items.push({ key: name, name, progress, ...(progress.detail === undefined ? {} : { detail: progress.detail }) });
  }
  return items;
}

/** When the last run committed and nothing is left to show, the time it finished; otherwise null. */
export function lastSuccessfulUpdateAt(status: SystemUpdateStatus | undefined): number | null {
  return status?.report?.state === 'committed' && updateStatusItems(status).length === 0 ? status.report.at : null;
}
